import qs from 'qs'

import { fetchExportFile } from '@features/expertReports'
import type { ExpertReportCompareWith, ReportExportFormat } from '@features/expertReports'

import { getCurrentLocale, getRequest } from '../../utils'
import type { ExpertTrialBalanceReport } from './types/expertTrialBalanceReport'

export const EXPERT_TRIAL_BALANCE_COMPARE_COUNT = 1

export interface ExpertTrialBalanceQueryParams {
  /** Encoded period string, e.g. `fiscalyear:<organizationId>,2026`. */
  period: string
  compareCount: number
  compareWith: ExpertReportCompareWith
  /** Server-side filter, default `false`. The UI speaks in "show", the wire in "hide". */
  hideZeroes: boolean
  /** Account number or label substring filter. Omitted from the request when empty or whitespace-only. */
  search?: string
}

export interface FetchExpertTrialBalanceParams extends ExpertTrialBalanceQueryParams {
  organizationId: string
}

export interface FetchExpertTrialBalanceExportParams extends ExpertTrialBalanceQueryParams {
  format: ReportExportFormat
  organizationId: string
}

export const buildExpertTrialBalanceQueryString = ({
  compareCount,
  compareWith,
  hideZeroes,
  period,
  search,
}: ExpertTrialBalanceQueryParams): string => {
  const trimmedSearch = search?.trim()

  return qs.stringify({
    compareCount,
    compareWith,
    hideZeroes,
    period,
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
  })
}

const getExpertTrialBalanceExportFallbackFileName = (format: ReportExportFormat): string => `Trial-balance.${format}`

export const fetchExpertTrialBalance = async ({
  compareCount,
  compareWith,
  hideZeroes,
  organizationId,
  period,
  search,
}: FetchExpertTrialBalanceParams): Promise<ExpertTrialBalanceReport> => {
  const queryString = buildExpertTrialBalanceQueryString({ compareCount, compareWith, hideZeroes, period, search })
  const { data } = await getRequest(`/v2/organizations/${organizationId}/reports/trialBalance?${queryString}`, {
    'accept-language': getCurrentLocale(),
  })

  return data
}

export const fetchExpertTrialBalanceExport = async ({
  compareCount,
  compareWith,
  format,
  hideZeroes,
  organizationId,
  period,
  search,
}: FetchExpertTrialBalanceExportParams) => {
  const queryString = buildExpertTrialBalanceQueryString({ compareCount, compareWith, hideZeroes, period, search })
  const path = `/v2/organizations/${organizationId}/reports/trialBalance.${format}?${queryString}`

  return fetchExportFile(path, format, getExpertTrialBalanceExportFallbackFileName(format))
}
